import { CustomCellEditorProps } from 'ag-grid-react';
import { useState } from 'react';

import {
  CalendarRange,
  type DateRange,
  type Matcher,
} from '@mint-lib/tw-components/patterns';

export interface DateRangeCellEditorProps<
  TData = unknown,
  TValue = DateRange,
  TContext = unknown,
> extends CustomCellEditorProps<TData, TValue, TContext> {
  /**
   * Callback that resets the selected date range. It will be called when the `Reset` button is clicked.
   */
  onValueReset?: (
    params: DateRangeCellEditorProps<TData, TValue, TContext>,
  ) => void;
  /**
   * Partial props of the `CalendarRange` component itself. Use it when you need to set `disabledDates`, `eventDates`, etc.
   */
  calendarRangeProps?: {
    disabledDates?: Matcher;
    eventDates?: Date[];
  };
}

const emptyRange: DateRange = { from: undefined, to: undefined };

export const DateRangeEditor = (props: DateRangeCellEditorProps) => {
  const [selected, setSelected] = useState<DateRange>(
    props.initialValue ?? emptyRange,
  );
  const [month, setMonth] = useState<Date | undefined>(selected.from);

  const { calendarRangeProps = {} } = props;

  const handleDateApply = (dateRange?: DateRange) => {
    props.onValueChange(dateRange);
    props.api.stopEditing();
  };

  const handleSelect = (dateRange?: DateRange) => {
    setSelected({ from: dateRange?.from, to: dateRange?.to });
    setMonth(dateRange?.from);
  };

  const handleMonthChange = (date: Date) => {
    setMonth(date);
  };

  const getResetHandler = () => {
    if (!props.onValueReset) {
      return;
    }

    return (dateRange?: DateRange) => {
      props.onValueReset?.({
        ...props,
        value: dateRange,
      });
    };
  };

  return (
    // The `translate-0` class is needed to fix the position of the calendar popup inside the grid. Without it, the popup will be cut off or positioned incorrectly.
    <div
      className="translate-0"
      data-dd-action-name="Grid cell: edit date range"
    >
      <CalendarRange
        {...calendarRangeProps}
        selected={selected}
        month={month}
        onMonthChange={handleMonthChange}
        onSelect={handleSelect}
        onApply={handleDateApply}
        onReset={getResetHandler()}
      />
    </div>
  );
};
